import {Map, List} from 'immutable';

import ArtTypes from '~/constants/types/ArtTypes';
import CanvasTypes from '~/constants/types/CanvasTypes';
import DesignTypes from '~/constants/types/DesignTypes';
import IconTypes from '~/constants/types/IconTypes';
import NamesAndNumbersTypes from '~/constants/types/NamesAndNumbersTypes';
import ProductTypes from '~/constants/types/ProductTypes';
import TextTypes from '~/constants/types/TextTypes';
import UploadTypes from '~/constants/types/UploadTypes';

import {makeUndoable} from '~/utils/undoable';

import DesignIconConstants from '~/constants/DesignIconConstants';
import {INK_COLORS} from '~/constants/InkConstants';
import {COLOR_MODES, PRODUCT_COLOR_REMOVALS} from '~/constants/UploadConstants';

import {
  ArtIcon,
  NamesIcon,
  NumbersIcon,
  TextIcon,
  UploadIcon
} from '~/data/records/icons';
import {
  addSpotColor,
  changeArtIconSpotColor,
  changeSpotColor,
  fitWithinConstraint,
  iconRearranger,
  move as moveIcon,
  removeProductColor,
  removeSpotColor,
  resetArtIconSpotColor,
  resize as resizeIcon,
  shift
} from '~/data/mutators/icon';
import {
  isArt,
  isClipartMultiColor,
  isNamesOrNumbers,
  isText,
  isUpload
} from '~/data/accessors/icon';
import {isSameColor, makeSpotColor} from '~/utils/colors';
import {getUnrotatedDimensions} from '~/shared/utils/math';
import {
  getBottomMostIcon,
  getIconAlignments,
  getLeftMostIcon,
  getRightMostIcon,
  getTopMostIcon,
  getMidPoint,
  getTotalWidth,
  getTotalHeight,
  getIconPosition
} from '~/utils/geometry';

function setFontHeight(text) {
  if (text.length <= DesignIconConstants.TEXT.SHORT_TEXT_LIMIT) {
    return DesignIconConstants.TEXT.DEFAULTS.LINE_HEIGHT;
  }
  if (text.length > DesignIconConstants.TEXT.MEDIUM_TEXT_LIMIT) {
    return DesignIconConstants.TEXT.DEFAULTS.LONG_TEXT_FONT_HEIGHT;
  }
  return DesignIconConstants.TEXT.DEFAULTS.MEDIUM_TEXT_FONT_HEIGHT;
}

function updateIconsById(icons, ids, updater) {
  return icons.map(icon => (ids.includes(icon.id) ? updater(icon) : icon));
}

export function initialState() {
  return Map();
}

export function icon(state = initialState(), action) {
  switch (action.type) {
    case ArtTypes.ADD_ART: {
      const {iconId, icon: iconData} = action.payload;
      const icon = new ArtIcon({
        ...iconData,
        id: iconId,
        sizeIsDirty: true
      });
      return state.set(iconId, icon);
    }

    case ArtTypes.SWAP: {
      const {
        iconId,
        blackwhite,
        clipartId,
        categoryPath,
        categoryTagId,
        color,
        colors,
        clipartVersions,
        width,
        height,
        naturalWidth,
        naturalHeight,
        singleColor,
        transparent
      } = action.payload;
      const {
        color: defaultColor,
        verticalFlip,
        horizontalFlip,
        singleColor: defaultSingleColor,
        transparent: defaultTransparent
      } = new ArtIcon();

      return state.update(iconId, icon => {
        return icon.set('clipartVersions', clipartVersions).merge({
          blackwhite,
          categoryPath,
          categoryTagId,
          clipartId,
          color: color ?? defaultColor,
          colors,
          height: height || icon.height,
          horizontalFlip,
          sizeIsDirty: true,
          singleColor: singleColor ?? defaultSingleColor,
          transparent: transparent ?? defaultTransparent,
          verticalFlip,
          width: width || icon.width,
          naturalWidth,
          naturalHeight,
          rendered: false
        });
      });
    }

    case ArtTypes.UPDATE_LOCK_RATIO: {
      const {iconId, lockRatio} = action.payload;
      return state.update(iconId, icon =>
        icon.merge({lockRatio, sizeIsDirty: true})
      );
    }

    case ArtTypes.UPDATE_TRANSPARENCY: {
      const {iconId, transparency} = action.payload;
      return state.update(iconId, icon =>
        icon.set('transparent', transparency)
      );
    }

    case DesignTypes.FETCH_SUCCESS:
    case DesignTypes.DUPLICATE_DESIGN:
      return state.merge(action.payload.icons);

    case CanvasTypes.ICON_RENDERED: {
      const {iconId, width, height} = action.payload;
      return state.update(iconId, icon => {
        const {rendered: _rendered} = icon;
        const rendered = !isText(icon.type) || icon.rendered;

        const updatedProps = {
          height: height || icon.height,
          width: width || icon.width,
          rendered
        };

        // /* Ideally we shuldn't need to only update natural dimensions only when
        // there is lockratio, but icon services do not support unlocked rotation yet
        // */
        if (
          !_rendered &&
          height &&
          width &&
          icon?.lockRatio &&
          !isText(icon.type)
        ) {
          const {rotation} = icon;
          const {naturalWidth, naturalHeight} = getUnrotatedDimensions({
            height,
            width,
            originalWidth: icon.naturalWidth || width,
            originalHeight: icon.naturalHeight || height,
            rotation
          });

          updatedProps.naturalWidth = naturalWidth;
          updatedProps.naturalHeight = naturalHeight;
        }

        updatedProps.sizeIsDirty = false;

        if (isText(icon.type)) {
          updatedProps.textIsUpdating = false;
        }

        return icon.merge(updatedProps);
      });
    }

    case TextTypes.REPOSITION_FOR_TEXT_EFFECTS: {
      const {iconId, y} = action.payload;
      return state.update(iconId, icon => icon.merge({y}));
    }

    case DesignTypes.CLEAR_DESIGN_DATA:
      return initialState();

    case IconTypes.ALIGN: {
      const {iconId, align} = action.payload;
      return state.update(iconId, icon =>
        icon.merge({
          align,
          sizeIsDirty: !!icon.rotation
        })
      );
    }

    case IconTypes.ALIGN_CENTER: {
      const {icons} = action.payload;
      icons.forEach(icon => {
        state = state.update(icon.id, icon =>
          icon.set('x', getMidPoint(icons).x)
        );
      });
      return state;
    }

    case IconTypes.ALIGN_MIDDLE: {
      const {icons} = action.payload;
      icons.forEach(icon => {
        state = state.update(icon.id, icon =>
          icon.set('y', getMidPoint(icons).y)
        );
      });
      return state;
    }

    case IconTypes.ALIGN_LEFT: {
      const {icons} = action.payload;
      const refIcon = getLeftMostIcon(icons);

      icons.forEach(icon => {
        state = state.update(icon.id, icon =>
          icon.set('x', getIconAlignments(refIcon, icon).left)
        );
      });
      return state;
    }

    case IconTypes.ALIGN_RIGHT: {
      const {icons} = action.payload;
      const refIcon = getRightMostIcon(icons);

      icons.forEach(icon => {
        state = state.update(icon.id, icon =>
          icon.set('x', getIconAlignments(refIcon, icon).right)
        );
      });
      return state;
    }

    case IconTypes.ALIGN_TOP: {
      const {icons} = action.payload;
      const refIcon = getTopMostIcon(icons);

      icons.forEach(icon => {
        state = state.update(icon.id, icon =>
          icon.set('y', getIconAlignments(refIcon, icon).top)
        );
      });
      return state;
    }

    case IconTypes.ALIGN_BOTTOM: {
      const {icons} = action.payload;
      const refIcon = getBottomMostIcon(icons);

      icons.forEach(icon => {
        state = state.update(icon.id, icon =>
          icon.set('y', getIconAlignments(refIcon, icon).bottom)
        );
      });
      return state;
    }

    case IconTypes.DISTRIBUTE_HORIZONTAL: {
      const {icons} = action.payload;
      const left = getLeftMostIcon(icons);
      const spacing = getTotalWidth(icons) / (icons.length - 1);

      const filtered = icons
        .filter(icon => {
          return (
            icon.id !== getLeftMostIcon(icons).id &&
            icon.id !== getRightMostIcon(icons).id
          );
        })
        .sort((a, b) => a.x - b.x);

      let prev = getIconPosition(left).right;
      filtered.forEach(icon => {
        state = state.update(icon.id, icon =>
          icon.set('x', prev + spacing + icon.width / 2)
        );
        prev = prev + spacing + icon.width;
      });
      return state;
    }

    case IconTypes.DISTRIBUTE_VERTICAL: {
      const {icons} = action.payload;
      const top = getTopMostIcon(icons);
      const spacing = getTotalHeight(icons) / (icons.length - 1);

      const filtered = icons
        .filter(icon => {
          return (
            icon.id !== getTopMostIcon(icons).id &&
            icon.id !== getBottomMostIcon(icons).id
          );
        })
        .sort((a, b) => a.y - b.y);

      let prev = getIconPosition(top).bottom;
      filtered.forEach(icon => {
        state = state.update(icon.id, icon =>
          icon.set('y', prev + spacing + icon.height / 2)
        );
        prev = prev + spacing + icon.height;
      });
      return state;
    }

    case IconTypes.CENTER: {
      const {iconIds} = action.payload;
      return updateIconsById(state, iconIds, icon => {
        return icon.set('x', 0);
      });
    }

    case IconTypes.CHANGE_COLOR: {
      const {iconId, color} = action.payload;
      return state.update(iconId, icon => icon.set('color', color));
    }

    case IconTypes.CROP: {
      const {iconId, crop, sizeIsDirty} = action.payload;
      return state.update(iconId, icon =>
        icon
          ? icon.merge({
              crop: Map(crop),
              sizeIsDirty
            })
          : icon
      );
    }

    case IconTypes.COPY:
    case IconTypes.CUT: {
      const {iconsMap} = action.payload;

      // Create a copy from every source icon with target id
      const copiedIconsById = iconsMap.reduce((object, item) => {
        object[item.targetIconId] = state
          .get(item.sourceIconId)
          .set('id', item.targetIconId);
        return object;
      }, {});

      return state.merge(copiedIconsById);
    }

    case IconTypes.MOVE: {
      const {transforms, constraints} = action.payload;

      transforms.forEach(({icon, move}) => {
        state = state.update(icon.id, icon => {
          return fitWithinConstraint(
            moveIcon(icon, move.x, move.y),
            constraints
          );
        });
      });

      return state;
    }

    case IconTypes.PASTE: {
      const {clipboardSource, iconsMap, viewId, viewConstraint} =
        action.payload;

      // Duplicate every icon from clipboard into given canvas with target id
      const pastedIconsById = iconsMap.reduce((object, item) => {
        const pastedIcon = state.get(item.sourceIconId)?.merge({
          id: item.targetIconId,
          viewId: viewId
        });

        object[item.targetIconId] = shift(
          pastedIcon,
          viewConstraint,
          clipboardSource
        );

        return object;
      }, {});

      return state.merge(pastedIconsById);
    }

    case IconTypes.DUPLICATE: {
      const {iconsMap, viewId, viewConstraint} = action.payload;
      const duplicateIconsById = iconsMap.reduce((acc, icon) => {
        const duplicatedIcon = state.get(icon.sourceIconId).merge({
          id: icon.targetIconId,
          viewId
        });

        acc[icon.targetIconId] = shift(
          duplicatedIcon,
          viewConstraint,
          undefined
        );

        return acc;
      }, {});

      return state.merge(duplicateIconsById);
    }

    case IconTypes.RESET_CLIPART_COLORS: {
      const {selectedIcon} = action.payload;

      selectedIcon.colors.toArray().forEach(color => {
        state = state.update(selectedIcon.id, icon =>
          resetArtIconSpotColor(icon, color.original)
        );
      });
      return state;
    }

    case IconTypes.RESIZE: {
      const {transforms, constraints} = action.payload;

      if (transforms === undefined) return state;

      transforms.forEach(({icon, scale, move}) => {
        state = state.update(icon.id, icon => {
          const resizedIcon = resizeIcon(icon, scale.x, scale.y);
          const movedIcon = move
            ? moveIcon(resizedIcon, move.x, move.y)
            : resizedIcon;
          return fitWithinConstraint(movedIcon, constraints);
        });
      });

      return state;
    }

    case IconTypes.RESTORE: {
      const {
        sourceIconId,
        targetIconId,
        viewId,
        iconOptions = {}
      } = action.payload;
      const sanitizedProps = {
        id: targetIconId,
        viewId,
        ...iconOptions
      };
      const sourceIcon = state.get(sourceIconId);
      const restoredIcon = sourceIcon.merge(sanitizedProps);
      return state.set(targetIconId, restoredIcon);
    }

    case IconTypes.ROTATE: {
      const {iconId, rotation} = action.payload;
      return state.update(iconId, icon => {
        const isUploadOrArt = isUpload(icon.type) || isArt(icon.type);
        const isSizeDirty = isUploadOrArt ? icon.rotation !== rotation : true;
        const options = {rotation, sizeIsDirty: isSizeDirty};
        if (isArt(icon.type) && rotation !== 0) {
          options.lockRatio = true;
        }
        if (isText(icon.type)) {
          options.rotation = rotation * -1;
        }
        return icon.merge(options);
      });
    }

    case IconTypes.SET_VIEW: {
      const {iconId, viewId} = action.payload;
      return state.update(iconId, icon => icon.set('viewId', viewId));
    }

    case IconTypes.UPDATE_HORIZONTAL_FLIP: {
      const {iconId, flip} = action.payload;
      return state.update(iconId, icon => icon.set('horizontalFlip', flip));
    }

    case IconTypes.UPDATE_SINGLE_COLOR: {
      const {iconId, color, singleColor} = action.payload;
      return state.update(iconId, icon => {
        let newIcon = icon.merge({singleColor, color});

        if (isClipartMultiColor(newIcon)) {
          // CGIR does not support keep-white for multicolor cliparts
          newIcon = newIcon.set('transparent', true);
        }

        return newIcon;
      });
    }

    case IconTypes.UPDATE_SINGLE_COLOR_AND_CONTRAST: {
      const {iconId, color, singleColor, contrast, invertContrast} =
        action.payload;
      return state.update(iconId, icon => {
        let newIcon = icon.merge({
          singleColor,
          color,
          contrast,
          invertContrast
        });

        if (isClipartMultiColor(newIcon)) {
          // CGIR does not support keep-white for multicolor cliparts
          newIcon = newIcon.set('transparent', true);
        }

        return newIcon;
      });
    }

    case IconTypes.UPDATE_CONTRAST: {
      const {iconId, contrast} = action.payload;
      return state.update(iconId, icon => {
        const newIcon = icon.set('contrast', contrast);
        if (contrast === null) {
          return newIcon.set('invertContrast', false);
        }
        return newIcon;
      });
    }

    case IconTypes.UPDATE_INVERT_CONTRAST: {
      const {iconId, invertContrast} = action.payload;
      return state.update(iconId, icon =>
        icon.set('invertContrast', invertContrast)
      );
    }

    case IconTypes.UPDATE_VERTICAL_FLIP: {
      const {iconId, flip} = action.payload;
      return state.update(iconId, icon => icon.set('verticalFlip', flip));
    }

    case IconTypes.UPDATE_POSITION: {
      const {iconId, viewId, x, y, width, height} = action.payload;
      return state.update(iconId, icon => {
        return icon.merge({
          x,
          viewId,
          ...(y ? {y} : {}),
          ...(width ? {width} : {}),
          ...(height ? {height} : {})
        });
      });
    }

    case IconTypes.TOGGLE_INVERTED_CLIPART: {
      const {icon, isInverted} = action.payload;
      const {clipartId, colors, blackwhite} =
        icon.clipartVersions[isInverted ? 'inverted' : 'default'];

      // Map current user colors to selected clipart version
      const mappedColors = colors.map(spotColor => {
        const currentColor = icon.colors.find(item =>
          isSameColor(item.original, spotColor.original)
        );

        if (currentColor) {
          return spotColor.set('user', currentColor.user);
        }

        return spotColor;
      });

      return state.update(icon.id, icon =>
        icon.merge({clipartId, blackwhite, colors: mappedColors})
      );
    }

    case NamesAndNumbersTypes.ADD_NAMES: {
      const {iconId, viewId, fontHeight, y, color} = action.payload;
      const namesIcon = new NamesIcon({
        id: iconId,
        viewId,
        fontHeight,
        y,
        color
      });
      return state.set(iconId, namesIcon);
    }

    case NamesAndNumbersTypes.ADD_NUMBERS: {
      const {iconId, viewId, fontHeight, y, color} = action.payload;
      const numbersIcon = new NumbersIcon({
        id: iconId,
        viewId,
        fontHeight,
        y,
        color
      });
      return state.set(iconId, numbersIcon);
    }

    case NamesAndNumbersTypes.ADD_NAMES_AND_NUMBERS: {
      const {
        color,
        namesFontHeight,
        namesIconId,
        namesY,
        numbersFontHeight,
        numbersIconId,
        numbersY,
        viewId
      } = action.payload;
      const namesIcon = new NamesIcon({
        id: namesIconId,
        viewId,
        fontHeight: namesFontHeight,
        y: namesY,
        color
      });
      const numbersIcon = new NumbersIcon({
        id: numbersIconId,
        viewId,
        fontHeight: numbersFontHeight,
        y: numbersY,
        color
      });
      return state.merge({
        [namesIconId]: namesIcon,
        [numbersIconId]: numbersIcon
      });
    }

    case ProductTypes.ADD_PRODUCT:
    case ProductTypes.REPLACE_PRODUCTS_CART_MODE:
    case ProductTypes.SWAP_PRODUCT: {
      const {
        views,
        sponsorship,
        constraints,
        guidelines,
        oldConstraints,
        newViewIcons,
        scales,
        oldScales,
        decorationMethod,
        styleType,
        changedProductCategory,
        rearrangeIcons = true,
        useSpecialGuidelineOnIconRearranger = true
      } = action.payload;

      if (!views) return state;
      return state
        .filter(
          icon =>
            icon !== null &&
            !!constraints[icon?.viewId] &&
            (sponsorship || !isNamesOrNumbers(icon?.type))
        )
        .map(icon => {
          if (!rearrangeIcons) return icon;
          return iconRearranger({
            icon,
            designDimensions: newViewIcons,
            oldConstraints,
            newConstraints: constraints,
            oldScales,
            newScales: scales,
            guidelines,
            decorationMethod,
            styleType,
            changedProductCategory,
            useSpecialGuideline: useSpecialGuidelineOnIconRearranger
          });
        });
    }

    case TextTypes.ADD_TEXT: {
      const {iconId, icon: iconData} = action.payload;
      const {font, lastFont, text} = iconData;
      const icon = new TextIcon({
        ...iconData,
        id: iconId,
        sizeIsDirty: true,
        value: text,
        fontHeight: setFontHeight(text),
        ...lastFont,
        ...font
      });

      return state.set(iconId, icon);
    }

    case TextTypes.UPDATE_EFFECT: {
      const {iconId, effectType, effectSetting, effectName} = action.payload;
      return state.update(iconId, icon =>
        icon.merge({
          effectType,
          effectSetting,
          effectName,
          sizeIsDirty: true
        })
      );
    }

    case TextTypes.UPDATE_FONT: {
      const {
        iconId,
        font,
        fontName,
        fontCategory,
        fontOutlineAllowed,
        fontMinHeight,
        iconFontHeight
      } = action.payload;

      const fontHeight =
        fontMinHeight <= iconFontHeight ? iconFontHeight : fontMinHeight;
      return state.update(iconId, icon =>
        icon.merge({
          font,
          fontName,
          fontCategory,
          fontHeight,
          fontMinHeight,
          fontOutlineAllowed,
          outlineColor: fontOutlineAllowed
            ? icon.outlineColor
            : INK_COLORS.None,
          outlineWidth: fontOutlineAllowed ? icon.outlineWidth : 0,
          sizeIsDirty: true
        })
      );
    }

    case TextTypes.UPDATE_FONT_CATEGORY: {
      const {iconId, fontCategory, fontOutlineAllowed} = action.payload;
      return state.update(iconId, icon =>
        icon.merge({fontCategory, fontOutlineAllowed})
      );
    }

    case TextTypes.UPDATE_FONT_HEIGHT: {
      const {iconId, fontHeight} = action.payload;
      return state.update(iconId, icon =>
        icon.merge({
          fontHeight,
          sizeIsDirty: true
        })
      );
    }

    case TextTypes.UPDATE_OUTLINE: {
      const {iconId, outlineColor, outlineWidth, defaultIconColor} =
        action.payload;

      return state.update(iconId, icon => {
        if (!outlineWidth && icon.get('color') === INK_COLORS.None) {
          icon = icon.set('color', defaultIconColor);
        }
        return icon.merge({
          outlineColor:
            outlineColor === undefined ? icon.outlineColor : outlineColor,
          outlineWidth:
            outlineWidth === undefined ? icon.outlineWidth : outlineWidth,
          sizeIsDirty: true
        });
      });
    }

    case TextTypes.UPDATE_FIT_TO_DIMENSIONS: {
      const {iconId, options} = action.payload;
      return state.update(iconId, icon =>
        icon.merge({
          ...options,
          sizeIsDirty: true
        })
      );
    }

    case TextTypes.UPDATE_TEXT: {
      const {iconId, text} = action.payload;
      return state.update(iconId, icon =>
        icon.merge({
          value: text,
          sizeIsDirty: true,
          textIsUpdating: true
        })
      );
    }

    case TextTypes.VERIFY_DIMENSIONS: {
      const {iconId} = action.payload;
      return state.update(iconId, icon => icon.set('verifying', true));
    }

    case TextTypes.VERIFY_ERROR: {
      const {iconId} = action.payload;
      return state.update(iconId, icon =>
        icon.merge({
          rendered: true,
          verifying: false
        })
      );
    }

    case TextTypes.VERIFY_SUCCESS: {
      const {iconId, fontHeight} = action.payload;
      const newState = fontHeight
        ? {
            fontHeight: fontHeight,
            sizeIsDirty: true,
            rendered: false,
            verifying: false,
            converted: true
          }
        : {
            sizeIsDirty: false,
            rendered: true,
            verifying: false,
            converted: true
          };
      return state.update(iconId, icon => icon.merge(newState));
    }

    case ArtTypes.CHANGE_SPOT_COLOR: {
      const {iconId, original, user, transparentToggle} = action.payload;
      return state.update(iconId, icon =>
        changeArtIconSpotColor(icon, original, user, transparentToggle)
      );
    }

    case UploadTypes.TOGGLE_PRODUCT_COLORS: {
      const {iconId, color, remove, threshold} = action.payload;
      return state.update(iconId, icon =>
        removeProductColor(icon, color, threshold, remove)
      );
    }

    case UploadTypes.CHANGE_SPOT_COLOR: {
      const {iconId, original, user, changeSelectedUser} = action.payload;
      return state.update(iconId, icon =>
        changeSpotColor(icon, original, user, changeSelectedUser)
      );
    }

    case UploadTypes.REMOVE_SPOT_COLOR: {
      const {iconId, spotColor} = action.payload;
      return state.update(iconId, icon => removeSpotColor(icon, spotColor));
    }

    case UploadTypes.ADD_SPOT_COLOR: {
      const {iconId, spotColor} = action.payload;
      return state.update(iconId, icon => addSpotColor(icon, spotColor));
    }

    case UploadTypes.RESET_UPLOAD: {
      const {iconId} = action.payload;
      const {
        verticalFlip,
        horizontalFlip,
        singleColor,
        rotation,
        removeProductColor
      } = new UploadIcon();
      return state.update(iconId, icon => {
        const sizeIsDirty = icon.rotation !== rotation;
        const nextIcon = icon.spotColors.reduce((modifiedIcon, spotColor) => {
          return changeSpotColor(
            modifiedIcon,
            spotColor.original,
            spotColor.palette,
            true
          );
        }, icon);
        return nextIcon.merge({
          horizontalFlip,
          removeProductColor,
          rotation,
          singleColor,
          sizeIsDirty,
          verticalFlip
        });
      });
    }

    case UploadTypes.UPDATE_COLOR_MODE: {
      const {iconId, colorMode} = action.payload;

      if (colorMode === COLOR_MODES.MONOCHROME) {
        const blackAndWhite = List([
          makeSpotColor(INK_COLORS.Black),
          makeSpotColor(INK_COLORS.White)
        ]);

        state = state.setIn([iconId, 'spotColors'], blackAndWhite);
      }

      return state.setIn([iconId, 'colorMode'], colorMode);
    }

    case UploadTypes.ADD_RECENT_UPLOAD:
    case UploadTypes.UPLOAD_COMPLETE: {
      const {icon: iconData, iconId, recentUploadCopyId} = action.payload;

      const productColor = action.payload.productColor;
      let icon = new UploadIcon({
        ...iconData,
        id: iconId,
        sizeIsDirty: true
      });

      if (icon.colorMode === COLOR_MODES.SPOT) {
        const productColors = icon.spotColors.filter(
          c => c.get('palette').name === productColor.name
        );
        const isSingleColor =
          icon.spotColors
            .map(c => c.get('palette'))
            .toSet()
            .count() <= 1;
        const isRemovableProduct = PRODUCT_COLOR_REMOVALS.includes(
          productColor.name
        );

        if (isRemovableProduct && !productColors.isEmpty() && !isSingleColor) {
          productColors.forEach(color => {
            icon = changeSpotColor(icon, color.original, INK_COLORS.None);
          });
        } else if (icon.backgroundColor) {
          icon = changeSpotColor(icon, icon.backgroundColor, INK_COLORS.None);
        }

        // by default we want to remove the product colors
        const {removeProductColors} = action.payload;
        if (removeProductColors) {
          const {productColor, productColorRemovalThreshold} = action.payload;
          icon = removeProductColor(
            icon,
            productColor,
            productColorRemovalThreshold,
            true
          );
        }
      }

      return state.merge({
        [iconId]: icon,
        [recentUploadCopyId]: icon.set('id', recentUploadCopyId)
      });
    }

    case UploadTypes.UPLOAD_AND_SWAP: {
      const {
        iconId,
        icon: {selectedIcon: previousIcon},
        icon: iconData
      } = action.payload;
      const {x, y, width, height} = previousIcon;

      const icon = new UploadIcon({
        ...iconData,
        id: iconId,
        sizeIsDirty: true,
        x,
        y,
        width,
        height
      });
      return state.set(iconId, icon).delete(previousIcon.id);
    }
    default:
      return state;
  }
}

export default makeUndoable(icon);
